/**
 * UranIA PA — Dashboard widget: Compliance check per progetto R&I in
 * lavorazione (option B della checklist).
 *
 * Visibile solo a ``beneficiario_ri``. In Fase 2 mostra un progetto
 * placeholder con i 5 controlli di compliance canonici per una proposta
 * R&I/FESR:
 *
 *   1. DNSH (Do No Significant Harm) — 6 obiettivi ambientali UE
 *   2. RNA — disponibilità de minimis / GBER cumulo
 *   3. GBER intensità d'aiuto entro soglia (Art. 25 R&S)
 *   4. Eleggibilità beneficiario (tipo ente, dimensione, sede)
 *   5. Criteri valutazione coperti (Excellence / Impact / Implementation)
 *
 * Stato per item: ``ok`` / ``warning`` / ``blocker`` / ``pending`` con
 * link "Verifica con AI" verso ``/chat-prova`` (skill specifica).
 *
 * Quando il BC #4 R&I Workspace atterrà (Fase 4), questo widget
 * riceverà la lista delle proposte reali del beneficiario tramite
 * ``GET /api/v1/proposte/ri`` e il check sarà calcolato dal Gateway
 * con skill dedicate (auto-refresh ad ogni save).
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import { principalIsBeneficiario } from '../../lib/auth/rbac.ts';

type ItemStatus = 'ok' | 'warning' | 'blocker' | 'pending';

interface ComplianceItem {
  readonly key: string;
  readonly status: ItemStatus;
  readonly link: string | null;
}

const ITEMS: readonly ComplianceItem[] = [
  { key: 'dnsh', status: 'pending', link: '/chat-prova' },
  { key: 'rna_de_minimis', status: 'warning', link: '/chat-prova' },
  { key: 'gber_intensita', status: 'ok', link: '/chat-prova' },
  { key: 'eligibility', status: 'ok', link: '/chat-prova' },
  { key: 'valutazione_criteri', status: 'pending', link: '/chat-prova' },
];

function StatusIcon(props: { readonly status: ItemStatus }): JSX.Element {
  if (props.status === 'ok') {
    return (
      <svg width="22" height="22" viewBox="0 0 16 16" fill="#198754" aria-hidden="true">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
      </svg>
    );
  }
  if (props.status === 'blocker') {
    return (
      <svg width="22" height="22" viewBox="0 0 16 16" fill="#dc3545" aria-hidden="true">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
      </svg>
    );
  }
  if (props.status === 'warning') {
    return (
      <svg width="22" height="22" viewBox="0 0 16 16" fill="#fd7e14" aria-hidden="true">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="#6c757d" strokeWidth="1" aria-hidden="true">
      <circle cx="8" cy="8" r="7.5" />
      <text x="8" y="11" textAnchor="middle" fontSize="9" fill="#6c757d" stroke="none">?</text>
    </svg>
  );
}

function StatusBadge(props: { readonly status: ItemStatus }): JSX.Element {
  const { t } = useTranslation();
  const map: Record<ItemStatus, { bg: string; color: string; border?: string; key: string }> = {
    ok: { bg: '#d1e7dd', color: '#0f5132', key: 'ok' },
    warning: { bg: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7', key: 'warning' },
    blocker: { bg: '#f8d7da', color: '#842029', key: 'blocker' },
    pending: { bg: '#e7e7e7', color: '#495057', key: 'pending' },
  };
  const cfg = map[props.status];
  return (
    <span
      className="badge"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: cfg.border,
        fontSize: '0.62rem',
        letterSpacing: '0.02em',
      }}
    >
      {t(`dashboard.ri_compliance.status.${cfg.key}`)}
    </span>
  );
}

export function DashboardRiProjectComplianceWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const principal = useAuthStore(selectPrincipal);

  if (!principalIsBeneficiario(principal)) {
    return null;
  }

  const okCount = ITEMS.filter((i) => i.status === 'ok').length;
  const blockingCount = ITEMS.filter((i) => i.status === 'blocker').length;
  const pendingCount = ITEMS.filter((i) => i.status === 'pending').length;
  const warningCount = ITEMS.filter((i) => i.status === 'warning').length;

  return (
    <article
      className="urania-dashboard__widget card h-100 shadow-sm border-0"
      data-testid="dashboard-widget-ri-compliance"
      aria-labelledby="urania-dashboard-ri-compliance-heading"
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
          <h2
            id="urania-dashboard-ri-compliance-heading"
            className="h5 card-title mb-0"
          >
            {t('dashboard.ri_compliance.title')}
          </h2>
          <span
            className="badge"
            style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffeaa7',
              fontSize: '0.62rem',
            }}
          >
            DEMO
          </span>
        </div>
        <p className="text-muted small mb-3">
          {t('dashboard.ri_compliance.subtitle')}
        </p>

        {/* Card progetto demo */}
        <div
          className="rounded p-3 mb-3"
          style={{
            backgroundColor: '#f8f9fa',
            borderLeft: '3px solid #0066cc',
          }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
            <strong className="small">
              {t('dashboard.ri_compliance.project.name')}
            </strong>
            <span className="badge bg-light text-dark border small">
              {t('dashboard.ri_compliance.project.draft')}
            </span>
          </div>
          <p className="small text-muted mb-2 lh-sm">
            {t('dashboard.ri_compliance.project.summary')}
          </p>
          <div className="d-flex flex-wrap gap-2 small">
            <span style={{ fontSize: '0.7rem' }}>
              <code>FESR 1.1.4</code>
            </span>
            <span className="text-muted">·</span>
            <span style={{ fontSize: '0.7rem' }}>TRL 4-6</span>
            <span className="text-muted">·</span>
            <span style={{ fontSize: '0.7rem' }}>€420k</span>
          </div>
        </div>

        {/* Riepilogo numerico */}
        <div className="d-flex flex-wrap gap-2 mb-3 small">
          <span className="badge" style={{ backgroundColor: '#d1e7dd', color: '#0f5132' }}>
            ✓ {okCount} {t('dashboard.ri_compliance.summary.ok')}
          </span>
          {warningCount > 0 && (
            <span
              className="badge"
              style={{
                backgroundColor: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeaa7',
              }}
            >
              ⚠ {warningCount} {t('dashboard.ri_compliance.summary.warning')}
            </span>
          )}
          {blockingCount > 0 && (
            <span className="badge" style={{ backgroundColor: '#f8d7da', color: '#842029' }}>
              ✗ {blockingCount} {t('dashboard.ri_compliance.summary.blocker')}
            </span>
          )}
          <span className="badge bg-light text-dark border">
            ⊙ {pendingCount} {t('dashboard.ri_compliance.summary.pending')}
          </span>
        </div>

        {/* Lista item */}
        <ul
          className="list-unstyled mb-0 d-flex flex-column gap-2"
          aria-label={t('dashboard.ri_compliance.aria_label')}
        >
          {ITEMS.map((item) => {
            const label = t(`dashboard.ri_compliance.items.${item.key}.label`);
            const desc = t(`dashboard.ri_compliance.items.${item.key}.description`);
            return (
              <li
                key={item.key}
                className="d-flex gap-2 align-items-start"
                data-testid={`ri-compliance-item-${item.key}`}
              >
                <span className="flex-shrink-0 mt-1">
                  <StatusIcon status={item.status} />
                </span>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-1">
                    <span className="small fw-semibold">{label}</span>
                    <StatusBadge status={item.status} />
                  </div>
                  <p
                    className="small text-muted mb-1 lh-sm"
                    style={{ fontSize: '0.78rem' }}
                  >
                    {desc}
                  </p>
                  {item.link !== null && (
                    <Link
                      to={item.link}
                      className="small text-decoration-none"
                      style={{ fontSize: '0.78rem' }}
                      data-testid={`ri-compliance-item-${item.key}-link`}
                    >
                      → {t('dashboard.ri_compliance.cta_verify')}
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="alert alert-light border mt-3 mb-0 small" role="note">
          <strong className="d-block mb-1" style={{ fontSize: '0.78rem' }}>
            {t('dashboard.ri_compliance.note.title')}
          </strong>
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
            {t('dashboard.ri_compliance.note.body')}
          </span>
        </div>
      </div>
    </article>
  );
}
